// client/src/coms/views/watch_together_parts/watchPermissions.js
// Watch Together runs over the LiveKit data channel, so every receiver checks what a sender may do. The
// rights come from the sender's participant attributes ('vox.perms'), which only the server can set (voice
// token / live updates), so a modified client cannot grant itself anything.
// Missing attribute (older server) = unknown = allowed, matching the behaviour before permissions existed.

export const WATCH_PERMS = {
  start: 'wt_start',
  add: 'wt_add',
  removeOwn: 'wt_remove_own',
  manageQueue: 'wt_manage_queue',
  control: 'wt_control_playback',
  transferHost: 'wt_transfer_host',
  manageSessions: 'wt_manage_sessions',
};

export function parsePermAttribute(attributes) {
  if (!attributes || typeof attributes !== 'object' || !Object.prototype.hasOwnProperty.call(attributes, 'vox.perms')) return null;
  return new Set(
    String(attributes['vox.perms'] || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  );
}

export function permsForIdentity(room, identity) {
  try {
    const id = String(identity || '');
    if (!room || !id) return null;
    const local = room.localParticipant;
    if (local && String(local.identity) === id) return parsePermAttribute(local.attributes);
    const p = room.remoteParticipants && room.remoteParticipants.get(id);
    if (!p) return new Set(); // not in the room: no rights
    return parsePermAttribute(p.attributes);
  } catch {
    return null;
  }
}

export function hasPerm(perms, key) {
  return perms === null || perms === undefined || perms.has(key);
}

// Is the change from prev to next allowed for this sender?
// prev / next: { queue: [{ id, addedByIdentity }], activeQueueId, host, controlMode }
export function validateWatchChange({ prev, next, sender, perms }) {
  const p = prev || {};
  const n = next || {};
  const prevQueue = Array.isArray(p.queue) ? p.queue : [];
  const nextQueue = Array.isArray(n.queue) ? n.queue : [];
  const prevIds = new Map(prevQueue.map((q) => [q.id, q]));
  const nextIds = new Set(nextQueue.map((q) => q.id));
  const manage = hasPerm(perms, WATCH_PERMS.manageQueue);

  const added = nextQueue.filter((q) => !prevIds.has(q.id));
  if (added.length) {
    if (!hasPerm(perms, WATCH_PERMS.add)) return { ok: false, reason: 'add' };
    if (!prevQueue.length && !hasPerm(perms, WATCH_PERMS.start)) return { ok: false, reason: 'start' };
    // New items must be attributed to their real sender.
    if (added.some((q) => q.addedByIdentity && String(q.addedByIdentity) !== String(sender))) return { ok: false, reason: 'attribution' };
  }
  const removed = prevQueue.filter((q) => !nextIds.has(q.id));
  for (const q of removed) {
    const own = String(q.addedByIdentity || '') === String(sender);
    if (!(manage || (own && hasPerm(perms, WATCH_PERMS.removeOwn)))) return { ok: false, reason: 'remove' };
  }
  // Reordering items that stay in the queue.
  const keptPrev = prevQueue.filter((q) => nextIds.has(q.id)).map((q) => q.id);
  const keptNext = nextQueue.filter((q) => prevIds.has(q.id)).map((q) => q.id);
  if (keptPrev.join('|') !== keptNext.join('|') && !manage) return { ok: false, reason: 'reorder' };

  // Choosing what plays (skip / make active) is a playback control. The active item moving on because it
  // was removed is part of the removal.
  const prevActive = p.activeQueueId || '';
  const nextActive = n.activeQueueId || '';
  if (prevActive && nextActive !== prevActive && nextIds.has(prevActive)) {
    if (!(manage || hasPerm(perms, WATCH_PERMS.control))) return { ok: false, reason: 'active' };
  }

  if (n.controlMode != null && p.controlMode != null && n.controlMode !== p.controlMode) {
    const isHost = String(p.host || '') === String(sender);
    if (!(hasPerm(perms, WATCH_PERMS.manageSessions) || isHost)) return { ok: false, reason: 'mode' };
  }
  if (n.host != null && p.host && String(n.host) !== String(p.host)) {
    const isHost = String(p.host) === String(sender);
    if (!(hasPerm(perms, WATCH_PERMS.manageSessions) || (isHost && hasPerm(perms, WATCH_PERMS.transferHost)))) return { ok: false, reason: 'host' };
  }
  return { ok: true };
}
